
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type SsaoBlurCrossShaderUniforms = {
	uSsaoMap: WebGLUniformLocation,
	uDepthMap: WebGLUniformLocation,
	uInvProjection: WebGLUniformLocation,
	uTexelSize: WebGLUniformLocation
};

export type SsaoBlurCrossShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type SsaoBlurCrossShaderSettings = string[];

export class SsaoBlurCrossShader extends ShaderBase<SsaoBlurCrossShaderUniforms, SsaoBlurCrossShaderAttributes> {

    private static fragmentShaderSource = `
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uSsaoMap;
uniform sampler2D uDepthMap;
uniform mat4 uInvProjection;
uniform vec2 uTexelSize;

// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Reversed-Z: the depth buffer maps near → 1.0 and far → 0.0. Cleared depth
// (= 0.0) means "no geometry was drawn" — callers should discard or treat this
// as background BEFORE calling these functions. The reconstruction itself
// still works at depth == 0.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.
//
// The math is reversal-agnostic: invViewProjection is the inverse of whichever
// projection the depth buffer was rendered with, and the perspective divide
// recovers the correct world position regardless of which side of the NDC z
// range each plane maps to.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---

// Single-pass 3x3 cross bilateral blur for SSAO. Replaces the old 2-pass
// separable 7-tap blur that ssao-blur ships — temporal accumulation now
// handles most of the noise reduction, and the smaller spatial kernel
// preserves sharper AO contact detail (corners, creases, contact shadows)
// instead of smearing it across multiple texels.
//
// Depth-aware ("cross bilateral") so the blur stops at depth discontinuities;
// view-space Z is the right metric — world-space distance breaks on tilted
// flat surfaces at distance.
void main(void) {
	float result = 0.0;
	float totalWeight = 0.0;

	float centerDepth = texture(uDepthMap, vTexCoord).r;
	// Reversed-Z: cleared depth == 0.0 means no geometry.
	float centerViewZ = (centerDepth <= 0.0) ? 0.0 : viewPosFromDepth(centerDepth, vTexCoord, uInvProjection).z;

	for (int y = -1; y <= 1; ++y) {
		for (int x = -1; x <= 1; ++x) {
			vec2 offset = vec2(float(x), float(y)) * uTexelSize;
			vec2 sampleCoord = vTexCoord + offset;
			float sampleAo = texture(uSsaoMap, sampleCoord).r;

			float sampleDepth = texture(uDepthMap, sampleCoord).r;
			float sampleViewZ = (sampleDepth <= 0.0) ? 0.0 : viewPosFromDepth(sampleDepth, sampleCoord, uInvProjection).z;
			float depthDiff = abs(centerViewZ - sampleViewZ);
			// Inverse-quadratic depth weight: 200 means a ~7cm view-space
			// Z difference halves the contribution, ~14cm cuts it to 20%.
			// View-space Z is in metres (engine convention), so this is
			// tuned for fine surface detail — increase for harsher edges,
			// decrease for stronger smoothing across mild contours.
			float depthWeight = 1.0 / (1.0 + depthDiff * depthDiff * 200.0);
			// Spatial weight in pixels²: 0.5 gives the centre tap weight 1,
			// edge taps 2/3, corner taps 1/2. Mild fall-off because the
			// 3×3 footprint is already small — most denoising is temporal.
			float spatialWeight = 1.0 / (1.0 + float(x * x + y * y) * 0.5);
			float weight = depthWeight * spatialWeight;

			result += sampleAo * weight;
			totalWeight += weight;
		}
	}

	fragColor = vec4(vec3(result / totalWeight), 1.0);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: SsaoBlurCrossShaderUniformsContainer;
    public attributesContainer: SsaoBlurCrossShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: SsaoBlurCrossShaderSettings) {
        super(ctx, SsaoBlurCrossShader.fragmentShaderSource, SsaoBlurCrossShader.vertexShaderSource, settings);
        this.uniformsContainer = new SsaoBlurCrossShaderUniformsContainer(ctx, this);
        this.attributesContainer = new SsaoBlurCrossShaderShaderAttributesContainer(ctx, this);
    }
}

class SsaoBlurCrossShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: SsaoBlurCrossShader) {
        super(ctx);
    }

    public setSsaoMap(texture: Texture) : SsaoBlurCrossShaderUniformsContainer {
        this.setTextureValue('uSsaoMap', this.shader.uniforms.uSsaoMap, texture, 'sampler2D');
        return this;
    }

    public setDepthMap(texture: Texture) : SsaoBlurCrossShaderUniformsContainer {
        this.setTextureValue('uDepthMap', this.shader.uniforms.uDepthMap, texture, 'sampler2D');
        return this;
    }

    public setInvProjection(value: mat4 | number[]) : SsaoBlurCrossShaderUniformsContainer {
        this.setValue('uInvProjection', this.shader.uniforms.uInvProjection, value, 'mat4');
        return this;
    }

    public setTexelSize(value: vec2 | number[]) : SsaoBlurCrossShaderUniformsContainer {
        this.setValue('uTexelSize', this.shader.uniforms.uTexelSize, value, 'vec2');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uSsaoMap', uniform: this.shader.uniforms.uSsaoMap, dataType: 'sampler2D' },
			{ name: 'uDepthMap', uniform: this.shader.uniforms.uDepthMap, dataType: 'sampler2D' }
        ];
    }
}

class SsaoBlurCrossShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: SsaoBlurCrossShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : SsaoBlurCrossShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : SsaoBlurCrossShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}